import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useLog from 'hooks/useLog';
import { AdminContentCategory } from 'types';
import { SearchHit, SearchResponse, SearchSortMode } from 'types/search';
import { TimeFilter } from 'util/search';

export const MIN_SEARCH_LENGTH = 2;
export const MAX_SEARCH_LENGTH = 512;
export const SEARCH_DEBOUNCE_MS = 250;
const DEFAULT_PER_PAGE = 20;

interface ContentSearchOptions {
  // The AM/PM control, which the server now answers rather than the browser
  // filtering the page it happens to hold. 'all' sends nothing.
  timeFilter?: TimeFilter;
  perPage?: number;
}

const useContentSearch = (
  categories: AdminContentCategory[],
  { timeFilter = 'all', perPage = DEFAULT_PER_PAGE }: ContentSearchOptions = {},
) => {
  const { fetchAPIWithLog } = useLog('ContentSearch');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SearchSortMode>('relevance');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // react-router re-mints `navigate` on every location change, and
  // fetchAPIWithLog is memoized on it. Depending on that identity would make
  // the effect below re-run, aborting and re-issuing the search, every time
  // picking a result navigates. Hold it in a ref so only the query moves it.
  const fetchRef = useRef(fetchAPIWithLog);
  fetchRef.current = fetchAPIWithLog;

  const trimmed = query.trim();
  const isSearching = trimmed.length >= MIN_SEARCH_LENGTH;
  const categoriesKey = useMemo(() => categories.join(','), [categories]);

  // Anything that changes which documents match invalidates the page number:
  // page 7 of a new result set is a page the reader never asked for, and on a
  // narrower set it is often empty, which reads as 'no matches'.
  //
  // Declared before the fetch below so its cleanup clears the pending debounce
  // timer. The stale-page request is never issued rather than issued and
  // aborted.
  useEffect(() => {
    setPage(1);
  }, [trimmed, sort, categoriesKey, timeFilter]);

  useEffect(() => {
    // Cancel whatever is in flight: a slow response for 'gam' must never
    // overwrite the results for 'gamma flip'
    abortRef.current?.abort();

    if (!isSearching) {
      setHits([]);
      setError(null);
      setLoading(false);
      setHasNextPage(false);
      setTotal(0);
      return;
    }

    // Middle tier is limited to MAX_SEARCH_LENGTH but catching here to avoid a call.
    if (trimmed.length > MAX_SEARCH_LENGTH) {
      setHits([]);
      setError(`Search queries are limited to ${MAX_SEARCH_LENGTH} characters`);
      setLoading(false);
      setHasNextPage(false);
      setTotal(0);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);

    // The debounce covers a page change too. 250ms behind a click is not free,
    // but one path beats two, and it collapses a double click on the arrow into
    // one request rather than two.
    const timer = setTimeout(() => {
      const params = new URLSearchParams({
        q: trimmed,
        page: String(page),
        perPage: String(perPage),
        sort,
      });
      if (timeFilter !== 'all') {
        params.set('session', timeFilter);
      }
      categoriesKey
        .split(',')
        .filter((c) => c.length > 0)
        .forEach((category) => params.append('category', category));

      void (async () => {
        try {
          const response = await fetchRef.current<SearchResponse>(
            `search?${params.toString()}`,
            { signal: controller.signal },
          );
          if (controller.signal.aborted) {
            return;
          }
          if (response?.error != null) {
            setError('Search is unavailable');
            setHits([]);
            setHasNextPage(false);
            setTotal(0);
          } else {
            setHits(response?.data ?? []);
            // Both come off the response rather than being inferred from the
            // row count: a full page is not evidence of a next one.
            setHasNextPage(response?.hasNextPage ?? false);
            setTotal(response?.total ?? 0);
            setError(null);
          }
        } catch (err) {
          if (!controller.signal.aborted) {
            console.error('search request failed', err);
            setError('Search is unavailable');
            setHits([]);
            setHasNextPage(false);
            setTotal(0);
          }
        } finally {
          if (!controller.signal.aborted) {
            setLoading(false);
          }
        }
      })();
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [trimmed, isSearching, categoriesKey, perPage, sort, page, timeFilter]);

  // Guarded on hasNextPage rather than on total, because the server also refuses
  // to page past its result window and reports that by withholding the flag.
  const nextPage = useCallback(() => {
    setPage((current) => (hasNextPage ? current + 1 : current));
  }, [hasNextPage]);

  const prevPage = useCallback(() => {
    setPage((current) => Math.max(1, current - 1));
  }, []);

  return {
    query,
    setQuery,
    sort,
    setSort,
    hits,
    loading,
    error,
    isSearching,
    page,
    hasNextPage,
    total,
    nextPage,
    prevPage,
  };
};

export default useContentSearch;
